import { createI18n } from 'vue-i18n'
import { fetchWithTimeout } from './fetchWithTimeout'
import { BUILTIN_FALLBACK_LOCALE, BUILTIN_FALLBACK_MESSAGES } from './builtinFallback'

/**
 * locale 文档**单次**拉取的超时预算（毫秒）。
 *
 * 沿革：`T-138` 立为 `1000`；**`T-732` 放宽到 `4000`**。
 *
 * ## 为什么 1000 太短（`T-732` 的实证，不是推演）
 *
 * `T-727`／`T-729` 两份**真浏览器 E2E** 都撞到：
 *
 * ```text
 * Error loading locale zh: AbortError: 请求超时：1000ms 内未完成（/locales/zh.json）
 * ```
 *
 * 1000ms 只覆盖「热缓存 + 快网」。首访真实环境里下面任一项都会越过它：Vite 冷转换
 * 200 KiB 的 `zh.json`、冷启网关/TLS 握手、蜂窝网/弱 Wi-Fi 首包。
 * 后果不是「慢一点」，而是：回落英文（`T-727` 读数）／极端时**裸键名直出**
 * （`T-729` 现场 `.conn-text = routes.device-simulator.top.connected`）—— 真实用户体验问题。
 *
 * ## 为什么取 4000（区间 3000–5000 内的取值依据）
 *
 * - **上界**：必须 `< 5000`。启动链既有就绪预算 `tests/e2e/readiness.ts` 的 `READY_MS = 5000`
 *   是「挂载」那一段的预算（`T-133` 建立），单次预算一旦 ≥ 5000，「挂起」就必然撑爆它
 *   （既有判据 `startupChain.spec.ts` ⑥ 亦以此钉死上界）。
 * - **下界**：≥ 3000 才有意义 —— 相对 1000 至少 3× 余量；4000 给到 **4×**，
 *   再加**一次重试**后单文件的有效容忍 ≈ `2×4000 + 200 = 8200ms`。
 * - **不取 5000 本身**：那会与上面的就绪预算**贴边**（单次预算等于整段预算 ⇒ 重试必然撑爆），
 *   且既有判据 ⑥ 要求**严格小于** 5000。
 */
export const LOCALE_FETCH_TIMEOUT_MS = 4000

/**
 * 单个 locale 文档的最大尝试次数（`T-732`）：**首次 ＋ 1 次重试**。
 *
 * 只重试 1 次而不是更多：locale JSON 是**不可变静态资源**，失败几乎都是「暂时性」的
 * （首包丢、网关刚起、连接被复用坏）。第 2 次已能吃掉绝大多数抖动；继续加次数换不来
 * 成功率，只会等比放大「网络全黑」时的白屏时长。
 */
export const LOCALE_FETCH_MAX_ATTEMPTS = 2

/**
 * 重试前的**固定**退避（毫秒，`T-732`）。
 *
 * 选**固定**而非渐进（1s→2s→4s）的理由：静态资源的失败不携带拥塞信号，
 * 渐进退避真正能帮到的只有「同一连接持续坏」这一种情形，而它恰是重试**救不了**的那类；
 * 反过来，渐进退避会把最常见的那类抖动（连接刚建立、首包丢）多等好几秒。
 * 200ms 足够让一个刚坏的连接/网关轮换到另一条路径。
 */
export const LOCALE_RETRY_BACKOFF_MS = 200

/**
 * 缺键时的**最后一道**可读面（`T-732`）：键名末段人性化，**永不**返回裸键名。
 *
 * 例：`routes.device-simulator.top.connected` ⇒ `Connected`
 *     （⛔ 不是 `routes.device-simulator.top.connected`）。
 *
 * 为什么需要它：内置兜底文案（`builtinFallback.ts`）只覆盖首屏高频面，剩下的键若直接
 * 交给 vue-i18n 的默认行为，`t()` 会**逐字返回键名**（正是 `T-729` 的破相）。
 */
export function humanizeMissingKey(key: string): string {
  // ⚠️ 退化输入（空串、`...`）**不得**把整个键名当末段返回 ⇒ 没有可用段时走中性占位。
  const segments = key.split('.').filter(Boolean)
  const last = segments.length > 0 ? (segments[segments.length - 1] ?? '') : ''
  const spaced = last
    .replace(/[_\-\s]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
  if (!spaced) return '—'
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * vue-i18n `missing` 处理器：缺键 ⇒ 末段人性化标签（⛔ 不返回键名本身）。
 *
 * ⚠️ **`raw` 伪语言必须保留原语义**：`public/locales/config.json` 注册的 `raw`（「RAW Key」，
 * `raw.json` 内容是 `{}`）是**故意**让界面显示原始 i18n 键的调试语言（`setLocale()` 还会把
 * `fallbackLocale` 也切成 `raw`）。⇒ 当解析语言是 `raw` 时，原样返回键名。
 */
function missingHandler(locale: string, key: string): string {
  if (String(locale) === 'raw') return String(key)
  return humanizeMissingKey(String(key))
}

/** 引擎内部的 `setTimeout` 包装（便于将来替换/注入；当前即全局定时器）。 */
function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => { setTimeout(resolve, ms) })
}

/**
 * Language configuration interface
 */
export interface LocaleConfig {
  code: string
  name: string
  nativeName: string
  file: string
}

export interface I18nConfig {
  defaultLocale: string
  fallbackLocale: string
  availableLocales: LocaleConfig[]
}

// Default config (will be overwritten by config.json)
let config: I18nConfig = {
  defaultLocale: 'zh',
  fallbackLocale: 'zh',
  availableLocales: []
}

// Create i18n instance.
//
// T-732：实例**一创建就装内置兜底文案**（不是空消息表）。理由：
//  - 空表 ⇒ locale JSON 一旦超时/失败，`t()` 逐字返回键名（`T-729` 现场 `.conn-text`）；
//  - 装上兜底后，「启动到 JSON 到达之间」与「JSON 永久失败」两段都有可读文案；
//  - 这不是第二条真相源：`loadLocaleMessages()` 成功时 `setLocaleMessage(locale, …)` 会
//    **整体替换**该语言的消息表 ⇒ 正常情况下兜底文案一行都不会出现在界面上。
//
// ⚠️ 两处**刻意的**类型选择（`vue-tsc` 实测，别"顺手改回去"）：
//  - `messages: {}` 必须保持空表起步：写成 `messages: { zh: … }` 字面量会把 `createI18n`
//    的语言类型收窄成字面量 `'zh'`，`setLocale()` / `fallbackLocale.value` 的既有字符串
//    赋值当场 TS2322／TS2769／TS2551 报九处 —— 收益为零（运行期行为完全一样）。
//  - 兜底文案用下面那一行 `setLocaleMessage()` 在**运行期**装入，避开上述类型收紧。
const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: BUILTIN_FALLBACK_LOCALE, // Will be updated after config loads
  fallbackLocale: BUILTIN_FALLBACK_LOCALE,
  messages: {}, // Start empty on purpose; see setLocaleMessage() right below
  // T-732：缺键 ⇒ 末段人性化标签（`raw` 伪语言除外），⛔ 永不裸显键名
  missing: missingHandler,
  missingWarn: false, // Suppress missing translation warnings
  fallbackWarn: false
})

// T-732：**立刻**装入默认语言的内置兜底文案 —— 于是「启动到 JSON 到达之间」与
// 「JSON 永久失败」两段都有可读文案，⛔ 不再是空消息表（空表 ⇒ `t()` 逐字返回键名）。
// 正常情况下 `loadLocaleMessages()` 成功时会用**整份** `zh.json` 覆盖它。
i18n.global.setLocaleMessage(BUILTIN_FALLBACK_LOCALE, BUILTIN_FALLBACK_MESSAGES)

/**
 * 拉一个 locale 文档，带**超时 ＋ 中止（`fetchWithTimeout`）＋ 失败重试（`T-732`）**。
 *
 * 成功（2xx）⇒ 返回 `Response`；否则抛最后一次的失败原因（超时/网络/非 2xx）。
 *
 * 重试策略：最多 `LOCALE_FETCH_MAX_ATTEMPTS` 次，每次都是**完整预算**（⛔ 不打折 —— 打折会把
 * 「慢但正常」重新误判成失败，正是 `T-732` 要修的形态）；两次之间固定退避
 * `LOCALE_RETRY_BACKOFF_MS`，且**每次失败都记录**（⛔ 不静默）。
 */
async function fetchLocaleFile(url: string): Promise<Response> {
  let lastError: unknown = new Error(`locale 文档拉取失败：${url}`)
  for (let attempt = 1; attempt <= LOCALE_FETCH_MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetchWithTimeout(url, { timeoutMs: LOCALE_FETCH_TIMEOUT_MS })
      if (response.ok) return response
      lastError = new Error(`locale 文档 HTTP ${response.status}：${url}`)
    } catch (error) {
      lastError = error
    }
    if (attempt < LOCALE_FETCH_MAX_ATTEMPTS) {
      console.warn(
        `locale fetch failed (attempt ${attempt}/${LOCALE_FETCH_MAX_ATTEMPTS})，` +
        `${LOCALE_RETRY_BACKOFF_MS}ms 后重试：${url}`,
        lastError
      )
      await sleep(LOCALE_RETRY_BACKOFF_MS)
    }
  }
  throw lastError
}

/**
 * Load i18n configuration from JSON
 *
 * T-138：改用 `fetchWithTimeout` —— 挂起（网络黑洞/网关未就绪）时**必然在预算内失败**，
 * 走下方既有 `catch` 分支返回默认 config（不再让 `await initI18n()` 永不返回 ⇒ 白屏）。
 * T-732：失败会先**重试 1 次**（`fetchLocaleFile`）；两次都失败才回落到内置默认 config。
 * 失败**不静默**：`console.warn`/`console.error` 保留。
 */
export async function loadI18nConfig(): Promise<I18nConfig> {
  try {
    const response = await fetchLocaleFile('/locales/config.json')
    config = await response.json()
    return config
  } catch (error) {
    console.warn('Failed to load i18n config, using defaults')
    console.error('Error loading i18n config:', error)
    return config
  }
}

/**
 * Load locale messages from JSON file
 * Returns false if loading fails (for fallback handling)
 *
 * T-138：同 `loadI18nConfig` —— 超时 ⇒ 走既有 `return false` 分支（上层据此回落/告警）。
 * T-732：失败会先**重试 1 次**；两次都失败 ⇒ `false`，调用方保留**内置兜底文案**（不裸显键名）。
 */
export async function loadLocaleMessages(locale: string): Promise<boolean> {
  try {
    // Find the file name for this locale
    const localeConfig = config.availableLocales.find(l => l.code === locale)
    const fileName = localeConfig?.file || `${locale}.json`

    const response = await fetchLocaleFile(`/locales/${fileName}`)
    const messages = await response.json()
    i18n.global.setLocaleMessage(locale, messages)
    return true
  } catch (error) {
    console.warn(`Failed to load locale: ${locale}`)
    console.error(`Error loading locale ${locale}:`, error)
    return false
  }
}

/**
 * Initialize i18n by loading config and all available locales
 *
 * `T-732`：**任何一步失败都不裸显键名** —— 回落语言失败时保留实例上已装好的内置兜底文案
 * （`builtinFallback.ts`）；非回落语言失败时复制回落语言的消息表；两者都缺的键由
 * `missingHandler` 人性化（不是键名）。
 */
export async function initI18n(): Promise<void> {
  // Step 1: Load configuration
  await loadI18nConfig()

  // Step 2: Set fallback locale from config
  i18n.global.fallbackLocale.value = config.fallbackLocale

  // Step 3: Load fallback locale first (ensures we always have messages)
  const fallbackLoaded = await loadLocaleMessages(config.fallbackLocale)
  if (!fallbackLoaded) {
    // T-732：此处**不再**是「空消息表 + 界面裸显键名」——实例上已有内置兜底文案。
    console.error('Critical: Failed to load fallback locale')
  }

  // Step 4: Load all other available locales
  const otherLocales = config.availableLocales
    .filter(l => l.code !== config.fallbackLocale)
    .map(l => l.code)

  await Promise.all(
    otherLocales.map(async (locale) => {
      const loaded = await loadLocaleMessages(locale)
      if (!loaded) {
        // If a locale fails to load, copy fallback messages
        console.warn(`Using fallback for locale: ${locale}`)
        const fallbackMessages = i18n.global.getLocaleMessage(config.fallbackLocale)
        i18n.global.setLocaleMessage(locale, fallbackMessages)
      }
    })
  )

  // Step 5: Restore saved locale preference or use default
  const savedLocale = localStorage.getItem('locale')
  const targetLocale = savedLocale && isLocaleAvailable(savedLocale)
    ? savedLocale
    : config.defaultLocale

  setLocale(targetLocale)
}

/**
 * Check if a locale is available
 */
export function isLocaleAvailable(locale: string): boolean {
  return config.availableLocales.some(l => l.code === locale)
}

/**
 * Get available locales list
 */
export function getAvailableLocales(): LocaleConfig[] {
  return config.availableLocales
}

/**
 * Get i18n configuration
 */
export function getI18nConfig(): I18nConfig {
  return config
}

/**
 * Set current locale
 */
export function setLocale(locale: string) {
  if (!isLocaleAvailable(locale)) {
    console.warn(`Locale ${locale} not available, using fallback`)
    locale = config.fallbackLocale
  }
  i18n.global.locale.value = locale
  localStorage.setItem('locale', locale)

  // Update HTML lang attribute
  const htmlLang = locale === 'zh' ? 'zh-CN' : locale === 'en' ? 'en' : locale
  document.documentElement.lang = htmlLang
  document.documentElement.setAttribute('xml:lang', htmlLang)

  const contentLanguageMeta = document.querySelector('meta[http-equiv="Content-Language"]')
  if (contentLanguageMeta) {
    contentLanguageMeta.setAttribute('content', htmlLang)
  }
  
  // For 'raw' locale, ensure fallback is also 'raw' so missing keys show the key itself
  if (locale === 'raw') {
    i18n.global.fallbackLocale.value = 'raw'
  } else {
    i18n.global.fallbackLocale.value = config.fallbackLocale
  }
}

/**
 * Get current locale
 */
export function getLocale(): string {
  return i18n.global.locale.value
}

/**
 * Helper to merge additional messages from API (for dynamic updates)
 */
export function mergeMessages(messages: Record<string, any>) {
  Object.keys(messages).forEach((locale) => {
    i18n.global.mergeLocaleMessage(locale, messages[locale])
  })
}

// Backwards compatibility alias
export const setMessages = mergeMessages

export default i18n
